import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "aws-saa",
  name: "AWS SAA資格",
  nameEn: "AWS SAA",
  seo: {
    title:
      "AWS SAA資格対策を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "AWS Solutions Architect Associate（SAA）はクラウドエンジニアの登竜門です。ゆめさくエンジニアなら設計原則からベストプラクティスまで実践的に対策できます。",
    url: "/columns/aws-saa-programming-school",
  },
  hero: {
    title: "AWS SAA資格を学べる女性向けプログラミングスクール",
    englishTitle: "Learn AWS SAA",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "AWS SAA資格" },
  ],
  overview: {
    heading: "AWS SAA資格とは？",
    paragraphs: [
      "AWS SAA（Solutions Architect Associate）は、AWSが公式に認定するクラウド資格の中で最も人気が高い資格です。AWSの主要サービスを使ったアーキテクチャ設計の知識を証明し、クラウドエンジニアの登竜門とされています。",
      "試験では、可用性・耐障害性・コスト最適化・パフォーマンス・セキュリティの5つの柱に基づいたアーキテクチャ設計が問われます。EC2・S3・RDS・VPC・IAMなど幅広いサービスの知識が必要です。",
      "AWS認定資格を持つエンジニアの平均年収は非保有者より約15〜20%高いというデータがあり、転職市場でも大きなアドバンテージになります。日本国内でもSAA保有者の需要は年々高まっています。",
    ],
    useCases: [
      {
        label: "転職・キャリアアップ",
        description:
          "AWS SAA取得は「クラウドの基礎が体系的に身についている」ことの証明になり、未経験からのクラウドエンジニア転職やキャリアアップに直結します。",
      },
      {
        label: "アーキテクチャ設計力の証明",
        description:
          "可用性・コスト・セキュリティを考慮した設計判断ができることを客観的に示せるため、プロジェクトでの信頼獲得につながります。",
      },
      {
        label: "上位資格へのステップ",
        description:
          "SAA取得後はSolutions Architect Professional（SAP）やDevOps Engineer Professionalなど、より専門的な上位資格への道が開けます。",
      },
      {
        label: "社内評価・報奨金",
        description:
          "多くのIT企業がAWS認定資格の取得に報奨金を設けており、取得することで社内評価の向上や昇給につながるケースも多いです。",
      },
    ],
  },
  whyLearn: {
    heading: "AWS SAA資格を学ぶべき3つの理由",
    items: [
      {
        title: "クラウド人材の需要急増で資格の価値が高い",
        description:
          "企業のクラウド移行が加速する中、AWS認定資格者は慢性的に不足しています。SAA取得者には資格手当を設ける企業も多く、年収アップに直結するコストパフォーマンスの高い資格です。",
      },
      {
        title: "体系的なクラウド知識が身につく",
        description:
          "SAA試験の学習を通じて、コンピュート・ストレージ・データベース・ネットワーク・セキュリティといったクラウドインフラ全般の知識を体系的に整理できます。実務で「なぜこの構成にするのか」を論理的に説明できるようになります。",
      },
      {
        title: "3年間有効でキャリアの武器になる",
        description:
          "AWS認定資格は3年間有効で、その間は常に履歴書に記載できるキャリアの武器になります。更新（再認定）も可能で、最新の知識を維持していることをアピールできます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでAWS SAA資格を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 5の応用〜上級としてAWS SAA資格対策を行います。Phase 5基礎で学んだAWSの実践知識をベースに、試験で問われるアーキテクチャ設計の考え方を体系的に学びます。",
      "実際にAWS環境を触りながら学ぶため、暗記ではなく理解に基づいた知識が身につきます。模擬試験や過去問演習を通じて試験本番への対策も万全です。",
    ],
    topics: [
      "Well-Architected Frameworkの5つの柱",
      "コンピュート設計（EC2・Lambda・ECS選定）",
      "ストレージ設計（S3・EBS・EFS・Glacier）",
      "データベース設計（RDS・DynamoDB・ElastiCache）",
      "ネットワーク設計（VPC・サブネット・セキュリティグループ）",
      "セキュリティ設計（IAM・KMS・CloudTrail）",
    ],
  },
  careerPaths: {
    heading: "AWS SAA資格を活かせるキャリア",
    items: [
      {
        title: "クラウドアーキテクト",
        description:
          "企業のシステムをAWS上で最適に設計するスペシャリストです。ビジネス要件を技術要件に落とし込み、可用性・コスト・セキュリティのバランスを取ったアーキテクチャを提案します。",
        salary: "600〜1,100万円",
      },
      {
        title: "クラウドエンジニア",
        description:
          "AWSインフラの構築・運用・最適化を担当します。SAA資格があることで設計判断の根拠を示せるため、チーム内での信頼度が高まり、リーダーポジションへのステップアップにつながります。",
        salary: "500〜900万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "3〜6ヶ月（暗記中心で定着しにくい）",
        luagate: "2〜3ヶ月（実践ベースで深く理解）",
      },
      {
        label: "挫折率",
        selfStudy: "サービスの多さに圧倒されて挫折しやすい",
        luagate: "試験範囲を優先度付けして効率的に学習",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "問題集を解くだけで手を動かさない",
        luagate: "実際のAWS環境で設計パターンをハンズオン体験",
      },
      {
        label: "質問対応",
        selfStudy: "「なぜこの構成なのか」の理由がわからない",
        luagate: "AWS有資格者のメンターが設計意図から丁寧に解説",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "AWS SAA試験の難易度はどれくらいですか？",
      answer:
        "IT未経験の方にとっては中〜上級レベルですが、ゆめさくエンジニアのカリキュラムでAWSの実践知識を学んだ後であれば、十分に合格を狙えます。合格ラインは1000点中720点で、しっかり対策すれば一発合格も可能です。",
    },
    {
      question: "AWS実務経験がなくても受験できますか？",
      answer:
        "はい、受験に実務経験の条件はありません。ただし公式にはAWSの1年以上の実務経験が推奨されています。ゆめさくエンジニアではハンズオンで実際にAWS環境を触るため、実務経験に近い知識を身につけたうえで受験に臨めます。",
    },
    {
      question: "AWS SAA取得にかかる費用は？",
      answer:
        "受験料は税込16,500円です。ゆめさくエンジニアの受講料に含まれるAWS学習を通じて自然と試験範囲をカバーできるため、別途高額な試験対策講座を購入する必要はありません。",
    },
    {
      question: "SAA取得後、次に目指すべき資格は何ですか？",
      answer:
        "キャリアの方向性によって異なりますが、設計力を深めたい方はSolutions Architect Professional（SAP）、DevOps方面に進みたい方はDevOps Engineer Professionalがおすすめです。セキュリティ分野に興味がある方はSecurity Specialtyも選択肢に入ります。",
    },
  ],
  relatedLanguages: [
    { slug: "aws", name: "AWS / クラウド" },
    { slug: "kubernetes", name: "Kubernetes" },
    { slug: "terraform", name: "Terraform" },
    { slug: "security", name: "セキュリティ" },
  ],
};
